import { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  TextInput,
  ScrollArea,
  UnstyledButton,
  Group,
  Text,
  Loader,
  Stack,
} from '@mantine/core';
import { IconBuildingBank, IconChevronDown, IconCheck, IconSearch } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { fetchMemberships, switchContext, type ContextResponse, type Membership } from './contextApi';

const roleLabel = (m: Membership) => m.role_description || m.role_name;

/** Переключатель активного контекста (контрагент + роль). В футере сайдбара. */
export function ContextSwitcher({ enabled }: { enabled: boolean }) {
  const [ctx, setCtx] = useState<ContextResponse | null>(null);
  const [switching, setSwitching] = useState(false);
  const [opened, setOpened] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (enabled) fetchMemberships().then(setCtx).catch(() => {});
  }, [enabled]);

  const active = useMemo(() => {
    if (!ctx) return null;
    return ctx.memberships.find((m) => m.id === ctx.active_membership_id) || ctx.memberships[0];
  }, [ctx]);

  const filtered = useMemo(() => {
    if (!ctx) return [];
    const q = query.trim().toLowerCase();
    const list = q
      ? ctx.memberships.filter(
          (m) =>
            (m.counterparty_name || '').toLowerCase().includes(q) ||
            roleLabel(m).toLowerCase().includes(q),
        )
      : ctx.memberships;
    // Активный — первым, далее по названию контрагента.
    return [...list].sort((a, b) => {
      if (active) {
        if (a.id === active.id) return -1;
        if (b.id === active.id) return 1;
      }
      return (a.counterparty_name || '').localeCompare(b.counterparty_name || '', 'ru');
    });
  }, [ctx, query, active]);

  if (!enabled || !ctx || !active || ctx.memberships.length === 0) return null;

  const doSwitch = async (m: Membership) => {
    if (m.id === active.id) {
      setOpened(false);
      return;
    }
    setSwitching(true);
    try {
      await switchContext(m.id);
      // полная перезагрузка — весь скоуп (меню/данные) пересчитается по новому контексту
      window.location.assign('/');
    } catch {
      setSwitching(false);
      notifications.show({ title: 'Ошибка', message: 'Не удалось переключить контекст', color: 'red' });
    }
  };

  const single = ctx.memberships.length === 1;

  const label = (
    <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
      <IconBuildingBank size={16} style={{ flexShrink: 0 }} />
      <div style={{ minWidth: 0 }}>
        <Text size="xs" fw={600} truncate>{active.counterparty_name}</Text>
        <Text c="dimmed" truncate style={{ fontSize: 10 }}>{roleLabel(active)}</Text>
      </div>
      {!single && <IconChevronDown size={14} style={{ flexShrink: 0 }} />}
      {switching && <Loader size={12} />}
    </Group>
  );

  if (single) {
    return <div style={{ maxWidth: 200, padding: '4px 8px' }}>{label}</div>;
  }

  const openPicker = () => {
    setQuery('');
    setOpened(true);
  };

  return (
    <>
      <UnstyledButton
        style={{ maxWidth: 200, padding: '4px 8px', borderRadius: 6 }}
        className="hover:bg-[var(--mantine-color-default-hover)]"
        onClick={openPicker}
        title="Сменить контрагента / роль"
      >
        {label}
      </UnstyledButton>

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title="Выбор контрагента и роли"
        size="lg"
        centered
      >
        <Stack gap="sm">
          <TextInput
            data-autofocus
            placeholder="Поиск по контрагенту или роли…"
            leftSection={<IconSearch size={16} />}
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
          />
          <Text size="xs" c="dimmed">
            {filtered.length} из {ctx.memberships.length}
          </Text>
          <ScrollArea.Autosize mah="55vh" type="auto">
            <Stack gap={4} pr="sm">
              {filtered.length === 0 && (
                <Text size="sm" c="dimmed" ta="center" py="md">
                  Ничего не найдено
                </Text>
              )}
              {filtered.map((m) => {
                const isActive = m.id === active.id;
                return (
                  <UnstyledButton
                    key={m.id}
                    onClick={() => doSwitch(m)}
                    disabled={switching}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 8,
                      background: isActive ? 'var(--mantine-color-default-hover)' : undefined,
                    }}
                    className="hover:bg-[var(--mantine-color-default-hover)]"
                  >
                    <Group gap="sm" wrap="nowrap" align="center">
                      <IconBuildingBank size={18} style={{ flexShrink: 0, opacity: 0.7 }} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <Text size="sm" fw={500} truncate>
                          {m.counterparty_name}
                        </Text>
                        <Text size="xs" c="dimmed" truncate>
                          {roleLabel(m)}
                        </Text>
                      </div>
                      {isActive && <IconCheck size={16} style={{ flexShrink: 0 }} />}
                    </Group>
                  </UnstyledButton>
                );
              })}
            </Stack>
          </ScrollArea.Autosize>
        </Stack>
      </Modal>
    </>
  );
}
